'use client'

import { useEffect } from 'react'
import { usePathname } from 'next/navigation'
import { buildPhoneClickEvent, buildWhatsAppClickEvent } from '@/lib/lead-tracking'
import { captureAttribution, getCurrentOrigen, getStoredAttribution, pushDataLayer } from '@/lib/lead-tracking-client'
import { getWhatsAppContext } from '@/lib/whatsapp-link'

/**
 * Site-wide tracking mounted once in the root shell:
 * - stores gclid / gbraid / wbraid / utm_* on every navigation,
 * - pushes `phone_click` for any tel: link (header, footer, markdown content…),
 * - pushes `whatsapp_click` for wa.me links not rendered by WhatsAppChatLink.
 */
export default function SiteTracking() {
  const pathname = usePathname()

  useEffect(() => {
    captureAttribution()
  }, [pathname])

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const link = (event.target as HTMLElement | null)?.closest?.('a[href]') as HTMLAnchorElement | null
      if (!link) return
      const href = link.getAttribute('href') || ''
      const path = window.location.pathname

      if (href.startsWith('tel:')) {
        pushDataLayer(buildPhoneClickEvent(path))
        return
      }
      const isWhatsApp = /^https?:\/\/(wa\.me|api\.whatsapp\.com|web\.whatsapp\.com)\//.test(href)
      if (isWhatsApp && link.dataset.whatsappTracked !== 'true') {
        // wa.me links inside content can't carry a Ref code: send the source without ref_id
        pushDataLayer(
          buildWhatsAppClickEvent({
            pagePath: path,
            servicio: getWhatsAppContext(path).servicio,
            origen: getCurrentOrigen(),
            utmCampaign: getStoredAttribution().utm_campaign,
          }),
        )
      }
    }

    document.addEventListener('click', onClick, { capture: true })
    return () => document.removeEventListener('click', onClick, { capture: true })
  }, [])

  return null
}
